import React, { useEffect, useRef, useState } from 'react';
import { SharedPageLayout } from './SharedPageLayout';
import './Home.css';
import './UnderConstruction.css';

const STATUS_MESSAGES = [
    'Inserting coin...',
    'Warming up the CRT...',
    'Polishing the pixels...',
    'Feeding the ghosts...',
    'Loading high scores...',
    'Almost there... press START'
];

const NEON_COLORS = ['#ff00cc', '#00ffff', '#ffff00', '#9d4dff'];

// Two-frame pixel sprites for the monsters (1 = filled pixel); frames differ only in the legs.
const MONSTER_SPRITES = [
    [
        ['00100100', '00011000', '00111100', '01101101', '11111111', '10111101', '10100101', '00011000'],
        ['00100100', '10011001', '10111101', '11101111', '11111111', '01111110', '00100100', '01000010']
    ],
    [
        ['00111100', '01111110', '11011011', '11111111', '01111110', '00100100', '01011010', '10000001'],
        ['00111100', '01111110', '11011011', '11111111', '01111110', '01011010', '10000001', '01000010']
    ],
    [
        ['10000001', '01000010', '01111110', '11011011', '11111111', '11111111', '10100101', '00100100'],
        ['10000001', '01000010', '01111110', '11011011', '11111111', '11111111', '01011010', '10000001']
    ]
];
const SPRITE_SIZE = 8;

// Draws a starfield, a scrolling perspective grid, a sparkle trail and a squash-the-monsters game on a canvas.
function useNeonCanvas(canvasRef, onStats) {
    const onStatsRef = useRef(onStats);
    onStatsRef.current = onStats;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return undefined;
        }
        const ctx = canvas.getContext('2d');
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let width = 0;
        let height = 0;
        let frameId = 0;
        let gridOffset = 0;
        const stars = [];
        const sparks = [];
        const monsters = [];
        const rings = [];
        const popups = [];
        let score = 0;
        let escaped = 0;
        let combo = 0;
        let lastKillTime = 0;
        let nextSpawn = 0;

        // Publishes the current score to the React HUD.
        const publishStats = () => onStatsRef.current && onStatsRef.current({ score, escaped });

        // Spawns a monster at a random spot with a random drift direction.
        const spawnMonster = (now) => {
            const angle = Math.random() * Math.PI * 2;
            const speed = 0.4 + Math.random() * 0.9 + Math.min(score / 400, 1.2);
            const scale = 4 + Math.floor(Math.random() * 4);
            const size = SPRITE_SIZE * scale;
            monsters.push({
                x: size + Math.random() * Math.max(1, width - size * 2),
                y: size + Math.random() * Math.max(1, height - size * 2),
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                scale,
                size,
                sprite: Math.floor(Math.random() * MONSTER_SPRITES.length),
                color: NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)],
                born: now,
                life: 4500 + Math.random() * 3500
            });
        };

        // Blows a monster up: particles, a shock ring and a floating score popup.
        const explode = (monster, now) => {
            const cx = monster.x + monster.size / 2;
            const cy = monster.y + monster.size / 2;
            for (let i = 0; i < 28; i += 1) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 1 + Math.random() * 5;
                sparks.push({
                    x: cx,
                    y: cy,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    life: 1,
                    decay: 0.016 + Math.random() * 0.02,
                    size: 2 + Math.random() * 4,
                    color: i % 4 === 0 ? '#ffffff' : monster.color
                });
            }
            rings.push({ x: cx, y: cy, radius: monster.size / 3, life: 1, color: monster.color });
            combo = now - lastKillTime < 1200 ? combo + 1 : 1;
            lastKillTime = now;
            const points = 10 * combo;
            score += points;
            popups.push({ x: cx, y: cy, text: combo > 1 ? `+${points} x${combo}` : `+${points}`, life: 1, color: monster.color });
            publishStats();
        };

        // Squashes the topmost monster under the pointer, if any.
        const handlePointerDown = (event) => {
            const now = performance.now();
            const pad = 10;
            for (let i = monsters.length - 1; i >= 0; i -= 1) {
                const monster = monsters[i];
                if (
                    event.clientX >= monster.x - pad && event.clientX <= monster.x + monster.size + pad &&
                    event.clientY >= monster.y - pad && event.clientY <= monster.y + monster.size + pad
                ) {
                    monsters.splice(i, 1);
                    explode(monster, now);
                    return;
                }
            }
        };

        // Draws one monster, popping in at birth and fading out before it escapes.
        const drawMonster = (monster, now) => {
            const age = now - monster.born;
            const remaining = monster.life - age;
            const popIn = Math.min(1, age / 250);
            const fadeOut = Math.min(1, remaining / 700);
            const eased = 1 + Math.sin(popIn * Math.PI) * 0.35;
            const scale = monster.scale * popIn * (popIn < 1 ? eased : 1);
            const frame = Math.floor(now / 350 + monster.born) % 2;
            const pattern = MONSTER_SPRITES[monster.sprite][frame];
            const offset = (monster.size - SPRITE_SIZE * scale) / 2;
            ctx.save();
            ctx.globalAlpha = Math.max(0, fadeOut);
            ctx.fillStyle = monster.color;
            ctx.shadowColor = monster.color;
            ctx.shadowBlur = 14;
            for (let row = 0; row < SPRITE_SIZE; row += 1) {
                for (let col = 0; col < SPRITE_SIZE; col += 1) {
                    if (pattern[row][col] === '1') {
                        ctx.fillRect(monster.x + offset + col * scale, monster.y + offset + row * scale, scale, scale);
                    }
                }
            }
            // Two white eyes for a bit of personality.
            ctx.shadowBlur = 0;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(monster.x + offset + 2 * scale, monster.y + offset + 3 * scale, scale, scale);
            ctx.fillRect(monster.x + offset + 5 * scale, monster.y + offset + 3 * scale, scale, scale);
            ctx.restore();
        };

        // Moves monsters, bounces them off the edges, spawns new ones and removes escaped ones.
        const updateMonsters = (now) => {
            const interval = Math.max(450, 1300 - score * 2);
            if (now >= nextSpawn && monsters.length < 12) {
                spawnMonster(now);
                nextSpawn = now + interval * (0.6 + Math.random() * 0.8);
            }
            for (let i = monsters.length - 1; i >= 0; i -= 1) {
                const monster = monsters[i];
                monster.x += monster.vx;
                monster.y += monster.vy;
                if (monster.x < 0 || monster.x + monster.size > width) {
                    monster.vx *= -1;
                    monster.x = Math.min(Math.max(monster.x, 0), width - monster.size);
                }
                if (monster.y < 0 || monster.y + monster.size > height) {
                    monster.vy *= -1;
                    monster.y = Math.min(Math.max(monster.y, 0), height - monster.size);
                }
                if (now - monster.born > monster.life) {
                    monsters.splice(i, 1);
                    escaped += 1;
                    publishStats();
                } else {
                    drawMonster(monster, now);
                }
            }
        };

        // Draws the expanding shock rings and floating score popups.
        const drawEffects = () => {
            ctx.save();
            for (let i = rings.length - 1; i >= 0; i -= 1) {
                const ring = rings[i];
                ring.radius += 4;
                ring.life -= 0.04;
                if (ring.life <= 0) {
                    rings.splice(i, 1);
                } else {
                    ctx.globalAlpha = ring.life;
                    ctx.strokeStyle = ring.color;
                    ctx.shadowColor = ring.color;
                    ctx.shadowBlur = 12;
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
                    ctx.stroke();
                }
            }
            ctx.font = 'bold 20px "Courier New", monospace';
            ctx.textAlign = 'center';
            for (let i = popups.length - 1; i >= 0; i -= 1) {
                const popup = popups[i];
                popup.y -= 1;
                popup.life -= 0.02;
                if (popup.life <= 0) {
                    popups.splice(i, 1);
                } else {
                    ctx.globalAlpha = popup.life;
                    ctx.fillStyle = popup.color;
                    ctx.shadowColor = popup.color;
                    ctx.shadowBlur = 8;
                    ctx.fillText(popup.text, popup.x, popup.y);
                }
            }
            ctx.restore();
        };

        // Resizes the canvas to the viewport and regenerates the stars.
        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            stars.length = 0;
            const count = Math.floor((width * height) / 9000);
            for (let i = 0; i < count; i += 1) {
                stars.push({
                    x: Math.random() * width,
                    y: Math.random() * height * 0.6,
                    size: Math.random() * 1.6 + 0.3,
                    phase: Math.random() * Math.PI * 2,
                    speed: Math.random() * 0.03 + 0.01
                });
            }
        };

        // Spawns a few colored sparks at the pointer position.
        const spawnSparks = (x, y) => {
            for (let i = 0; i < 3; i += 1) {
                sparks.push({
                    x,
                    y,
                    vx: (Math.random() - 0.5) * 3,
                    vy: (Math.random() - 0.5) * 3 - 0.5,
                    life: 1,
                    color: NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)]
                });
            }
            if (sparks.length > 250) {
                sparks.splice(0, sparks.length - 250);
            }
        };

        const handlePointerMove = (event) => spawnSparks(event.clientX, event.clientY);

        // Renders the retro sun sitting on the horizon.
        const drawSun = (horizon) => {
            const radius = Math.min(width, height) * 0.18;
            const cx = width / 2;
            const cy = horizon - radius * 0.25;
            const gradient = ctx.createLinearGradient(0, cy - radius, 0, cy + radius);
            gradient.addColorStop(0, '#ffff00');
            gradient.addColorStop(0.5, '#ff00cc');
            gradient.addColorStop(1, '#6a00ff');
            ctx.save();
            ctx.beginPath();
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
            ctx.clip();
            ctx.globalAlpha = 0.85;
            ctx.fillStyle = gradient;
            ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
            // Horizontal slits that give the sun its classic striped look.
            ctx.globalCompositeOperation = 'destination-out';
            for (let i = 0; i < 7; i += 1) {
                const y = cy + radius * 0.05 + i * radius * 0.13;
                ctx.fillRect(cx - radius, y, radius * 2, 2 + i * 1.6);
            }
            ctx.restore();
        };

        // Renders the perspective grid that scrolls toward the viewer.
        const drawGrid = (horizon) => {
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 0, 204, 0.55)';
            ctx.shadowColor = '#ff00cc';
            ctx.shadowBlur = 8;
            ctx.lineWidth = 1.2;
            const cx = width / 2;
            for (let i = -20; i <= 20; i += 1) {
                ctx.beginPath();
                ctx.moveTo(cx + i * 18, horizon);
                ctx.lineTo(cx + i * width * 0.12, height);
                ctx.stroke();
            }
            const rows = 14;
            for (let i = 0; i < rows; i += 1) {
                const t = ((i + gridOffset) % rows) / rows;
                const y = horizon + (height - horizon) * t * t;
                ctx.globalAlpha = 0.25 + t * 0.75;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(width, y);
                ctx.stroke();
            }
            ctx.restore();
        };

        // Main animation loop.
        const render = (time) => {
            ctx.clearRect(0, 0, width, height);
            const horizon = height * 0.62;

            stars.forEach((star) => {
                const twinkle = 0.5 + 0.5 * Math.sin(time * star.speed * 0.1 + star.phase);
                ctx.globalAlpha = 0.2 + twinkle * 0.8;
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(star.x, star.y, star.size, star.size);
            });
            ctx.globalAlpha = 1;

            drawSun(horizon);
            drawGrid(horizon);

            for (let i = sparks.length - 1; i >= 0; i -= 1) {
                const spark = sparks[i];
                spark.x += spark.vx;
                spark.y += spark.vy;
                spark.vy += 0.04;
                spark.life -= spark.decay || 0.02;
                if (spark.life <= 0) {
                    sparks.splice(i, 1);
                } else {
                    ctx.globalAlpha = spark.life;
                    ctx.fillStyle = spark.color;
                    ctx.shadowColor = spark.color;
                    ctx.shadowBlur = 10;
                    ctx.fillRect(spark.x, spark.y, spark.size || 3, spark.size || 3);
                }
            }
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;

            if (!reduceMotion) {
                updateMonsters(time);
                drawEffects();
            }

            if (!reduceMotion) {
                gridOffset += 0.02;
                frameId = requestAnimationFrame(render);
            }
        };

        resize();
        frameId = requestAnimationFrame(render);
        window.addEventListener('resize', resize);
        if (!reduceMotion) {
            window.addEventListener('pointermove', handlePointerMove);
            window.addEventListener('pointerdown', handlePointerDown);
            document.body.style.cursor = 'crosshair';
        }

        return () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener('resize', resize);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerdown', handlePointerDown);
            document.body.style.cursor = '';
        };
    }, [canvasRef]);
}

// Types out the status messages one after the other in a loop.
function useTypewriter(messages) {
    const [text, setText] = useState('');

    useEffect(() => {
        let messageIndex = 0;
        let charIndex = 0;
        let deleting = false;
        let timeoutId;

        // Advances the typing state machine by one step.
        const tick = () => {
            const message = messages[messageIndex];
            let delay = deleting ? 25 : 60;
            if (!deleting) {
                charIndex += 1;
                if (charIndex === message.length) {
                    deleting = true;
                    delay = 1400;
                }
            } else {
                charIndex -= 1;
                if (charIndex === 0) {
                    deleting = false;
                    messageIndex = (messageIndex + 1) % messages.length;
                    delay = 300;
                }
            }
            setText(message.slice(0, charIndex));
            timeoutId = setTimeout(tick, delay);
        };

        timeoutId = setTimeout(tick, 500);
        return () => clearTimeout(timeoutId);
    }, [messages]);

    return text;
}

// Renders a placeholder home page while the main content is being rebuilt.
export function UnderConstruction() {
    const canvasRef = useRef(null);
    const [stats, setStats] = useState({ score: 0, escaped: 0 });
    useNeonCanvas(canvasRef, setStats);
    const statusText = useTypewriter(STATUS_MESSAGES);

    return (
        <SharedPageLayout
            brand="GIUGGIOLI.NET"
            links={[]}
            footerText="Powered by Giuggioli.Net"
        >
            <canvas ref={canvasRef} className="uc-canvas" aria-hidden="true" />
            <div className="uc-hud" aria-live="off">
                <span>SCORE {String(stats.score).padStart(5, '0')}</span>
                <span>ESCAPED {stats.escaped}</span>
            </div>
            <header className="retro-hero uc-hero">
                <div className="retro-hero__content uc-content">
                    <p className="retro-kicker">coming soon</p>
                    <h1 className="retro-title uc-glitch" data-text="Under Construction">
                        Under Construction
                    </h1>
                    <p className="uc-status" aria-live="polite">
                        {statusText}
                        <span className="uc-cursor" aria-hidden="true">█</span>
                    </p>
                    <div className="uc-progress" role="presentation">
                        <div className="uc-progress__bar" />
                    </div>
                    <p className="retro-subtitle uc-subtitle">
                        
                    </p>
                </div>
            </header>
        </SharedPageLayout>
    );
}
